import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ExclamationIcon } from "@heroicons/react/outline";
import { useFormikContext } from "formik";
import SelectorField from "src/components/Form/SelectorField";
import TextInputField from "src/components/Form/TextInputField";
import { useStepField } from "../../../hooks/useStepField";
import useVariables from "../../../hooks/useVariables";
import FormatGroupLabelVariable from "../../FormatGroupLabelVariable";

function FlowBuilderOptionsFromVariable({
  currentStepIndex,
  maxOptionsLength,
}) {
  const [t] = useTranslation();
  const { setFieldValue } = useFormikContext();
  const [optionsFromVariable] = useStepField({
    index: currentStepIndex,
    name: "options_from_variable",
  });

  const { options, hasMissingActionTest, handleGetOptionsFromVariableData } =
    useVariables({
      currentStepIndex,
      allowedVariableTypes: ["step"],
      allowedVariableDataTypes: ["list"],
    });

  const optionsFromSelectedVariable = useMemo(
    () => handleGetOptionsFromVariableData(optionsFromVariable),
    [optionsFromVariable, handleGetOptionsFromVariableData]
  );

  const handleSelectDataSourceVariable = useCallback(() => {
    setFieldValue(`steps.${currentStepIndex}.option_from_variable_value`, null);
    setFieldValue(`steps.${currentStepIndex}.option_from_variable_title`, null);
    setFieldValue(
      `steps.${currentStepIndex}.option_from_variable_description`,
      null
    );
  }, [currentStepIndex, setFieldValue]);

  return (
    <div>
      {hasMissingActionTest && (
        <div className="my-2 flex items-center rounded-md bg-yellow-50 p-4">
          <ExclamationIcon
            className="h-5 w-5 text-yellow-400"
            aria-hidden="true"
          />
          <div className="ml-3">
            <div className="text-xs text-yellow-700">
              To use results of an action step, a test request is required to
              map available variables.
            </div>
          </div>
        </div>
      )}
      {maxOptionsLength && (
        <div className="my-2 flex items-center rounded-md bg-yellow-50 p-4">
          <ExclamationIcon
            className="h-5 w-5 text-yellow-400"
            aria-hidden="true"
          />
          <div className="ml-3">
            <div className="text-xs text-yellow-700">
              Only the first {maxOptionsLength} results of the variable will be
              used.
            </div>
          </div>
        </div>
      )}

      <div className="flex w-full flex-col space-y-4 py-2">
        <SelectorField
          name={`steps.${currentStepIndex}.options_from_variable`}
          label={t(
            "flows:steps.question.options-from-variable.label",
            "Data source for options"
          )}
          placeholder={t(
            "flows:steps.question.options-from-variable.placeholder",
            "Select variable"
          )}
          options={options}
          formatGroupLabel={FormatGroupLabelVariable}
          onSelect={handleSelectDataSourceVariable}
        />
        <SelectorField
          name={`steps.${currentStepIndex}.option_from_variable_value`}
          label={t(
            "flows:steps.question.option-from-variable-value.label",
            "Value of each option"
          )}
          placeholder={t(
            "flows:steps.question.option-from-variable-value.placeholder",
            "Select value"
          )}
          options={optionsFromSelectedVariable}
          formatGroupLabel={FormatGroupLabelVariable}
        />
        <SelectorField
          name={`steps.${currentStepIndex}.option_from_variable_title`}
          label={t(
            "flows:steps.question.option-from-variable-title.label",
            "Title of each option"
          )}
          placeholder={t(
            "flows:steps.question.option-from-variable-title.placeholder",
            "Select title"
          )}
          options={optionsFromSelectedVariable}
          formatGroupLabel={FormatGroupLabelVariable}
        />
        <SelectorField
          name={`steps.${currentStepIndex}.option_from_variable_description`}
          label={t(
            "flows:steps.question.option-from-variable-description.label",
            "Longer description for each option"
          )}
          placeholder={t(
            "flows:steps.question.option-from-variable-description.placeholder",
            "Select description"
          )}
          optional={true}
          options={optionsFromSelectedVariable}
          formatGroupLabel={FormatGroupLabelVariable}
        />
        <TextInputField
          type="text"
          name={`steps.${currentStepIndex}.missing_options_message`}
          label={t(
            "flows:steps.question.missing-options-message.label",
            "Message to display when no options were found"
          )}
          placeholder={t(
            "flows:steps.question.missing-options-message.placeholder",
            "No options can be found..."
          )}
        />
        {/* TODO: Add functionality to select which step to go when no options exist */}
      </div>
    </div>
  );
}

export default FlowBuilderOptionsFromVariable;
